import React from "react";
import {
  Box,
  CircularProgress,
  Skeleton,
  LinearProgress,
  Backdrop,
  Typography
} from "@mui/material";

/**
 * Production Loading Component
 *
 * Supports:
 * - circular spinner
 * - linear progress
 * - skeleton loaders
 * - card loaders
 * - course loaders
 * - discussion loaders
 */

const LoadingSpinner = ({
  type = "circular",
  size = 40,
  color = "primary",
  text = "",
  fullScreen = false,
  overlay = false,
  height = 200,
  count = 1,
  variant = "rectangular",
  width = "100%"
}) => {

  /* -----------------------------
  FULLSCREEN / OVERLAY LOADER
  ----------------------------- */

  if (type === "circular" && (fullScreen || overlay)) {
    return (
      <Backdrop
        open
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}
      >
        <CircularProgress size={size} color={color} />
        {text && (
          <Typography variant="body1">
            {text}
          </Typography>
        )}
      </Backdrop>
    );
  }

  /* -----------------------------
  SIMPLE CIRCULAR LOADER
  ----------------------------- */

  if (type === "circular") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height,
          width: "100%",
          gap: 2
        }}
      >
        <CircularProgress size={size} color={color} />

        {text && (
          <Typography variant="body2" color="text.secondary">
            {text}
          </Typography>
        )}
      </Box>
    );
  }

  /* -----------------------------
  LINEAR LOADER
  ----------------------------- */

  if (type === "linear") {
    return (
      <Box sx={{ width: "100%" }}>
        <LinearProgress color={color} />
      </Box>
    );
  }

  /* -----------------------------
  GENERIC SKELETON
  ----------------------------- */

  if (type === "skeleton") {
    return (
      <Box sx={{ width }}>
        {Array.from({ length: count }).map((_, i) => (
          <Skeleton
            key={i}
            variant={variant}
            width={width}
            height={height}
            animation="wave"
            sx={{ mb: 2 }}
          />
        ))}
      </Box>
    );
  }

  /* -----------------------------
  CARD SKELETON
  ----------------------------- */

  if (type === "card") {
    return (
      <Box>
        {Array.from({ length: count }).map((_, i) => (
          <Box
            key={i}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              mb: 2
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ ml: 2, flex: 1 }}>
                <Skeleton width="60%" />
                <Skeleton width="40%" />
              </Box>
            </Box>

            <Skeleton width="100%" />
            <Skeleton width="95%" />
            <Skeleton width="90%" />

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Skeleton variant="rectangular" width={80} height={32} />
              <Skeleton variant="rectangular" width={80} height={32} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  /* -----------------------------
  COURSE CARD SKELETON
  ----------------------------- */

  if (type === "course") {
    return (
      <Box>
        {Array.from({ length: count }).map((_, i) => (
          <Box
            key={i}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
              mb: 2
            }}
          >
            <Skeleton variant="rectangular" width="100%" height={140} />

            <Box sx={{ p: 2 }}>
              <Skeleton width="40%" />
              <Skeleton width="80%" />
              <Skeleton width="60%" />

              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <Skeleton variant="rectangular" width={60} height={24} />
                <Skeleton variant="rectangular" width={60} height={24} />
                <Skeleton variant="rectangular" width={60} height={24} />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  /* -----------------------------
  DISCUSSION SKELETON
  ----------------------------- */

  if (type === "discussion") {
    return (
      <Box>
        {Array.from({ length: count }).map((_, i) => (
          <Box
            key={i}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              mb: 2
            }}
          >
            <Box sx={{ display: "flex", mb: 2 }}>
              <Skeleton variant="circular" width={48} height={48} />

              <Box sx={{ ml: 2, flex: 1 }}>
                <Skeleton width="30%" />
                <Skeleton width="20%" />
              </Box>
            </Box>

            <Skeleton width="70%" />
            <Skeleton width="100%" />
            <Skeleton width="95%" />

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Skeleton variant="rectangular" width={60} height={32} />
              <Skeleton variant="rectangular" width={60} height={32} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  /* -----------------------------
  FALLBACK
  ----------------------------- */

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height,
        width: "100%"
      }}
    >
      <CircularProgress size={size} color={color} />
    </Box>
  );
};

export default LoadingSpinner;
